import { compareEventDisplayOrder } from '@tov-plus/domain';
import { msg, TovIcon } from '@tov-plus/ui';
import { NavLink } from '@tov-plus/frontend-data';

import type { GuestExperience } from '../lib/experience/guestExperience';
import { formatGuestDateTime } from '../lib/localization/guestLocale';
import { resolveWebsiteNavPath } from '../components/AppShell';
import {
  getSectionSummary,
  getSectionTargetPath,
} from './guestHomePageSectionHelpers';

type WebsiteSection = GuestExperience['guestWebsite']['sections'][number];

function findSection(sections: WebsiteSection[], type: WebsiteSection['type']) {
  return (
    sections.find((section) => section.enabled && section.type === type) ?? null
  );
}

function findSections(
  sections: WebsiteSection[],
  types: WebsiteSection['type'][],
) {
  return sections
    .filter((section) => section.enabled && types.includes(section.type))
    .sort((a, b) => a.order_index - b.order_index);
}

function stringField(
  section: WebsiteSection | null,
  key: string,
): string | null {
  if (!section) {
    return null;
  }
  const value = section.content_payload[key];
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function sectionLabel(section: WebsiteSection, fallback: string) {
  return section.anchor_label ?? stringField(section, 'title') ?? fallback;
}

function sectionBody(section: WebsiteSection) {
  return (
    stringField(section, 'body') ??
    stringField(section, 'summary') ??
    stringField(section, 'description') ??
    getSectionSummary(section)
  );
}

function daysUntil(iso: string) {
  const now = new Date();
  const target = new Date(iso);
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.ceil((target.getTime() - now.getTime()) / msPerDay);
}

export function GuestLandingPage({
  experience,
}: {
  experience: GuestExperience;
}) {
  const website = experience.guestWebsite;
  const sections = website.sections;
  const localeCode = experience.localizationContext.resolvedLocaleCode;
  const timezone = experience.occasion.timezone;

  const heroSection = findSection(sections, 'hero');
  const storySection =
    findSection(sections, 'story') ?? findSection(sections, 'welcome');
  const rsvpSection = findSection(sections, 'rsvp');
  const registrySection = findSection(sections, 'registry');
  const gallerySection = findSection(sections, 'gallery_preview');
  const travelSections = findSections(sections, [
    'travel',
    'accommodations',
    'maps',
    'shuttle_parking',
  ]);
  const faqSections = findSections(sections, ['faq']);

  const upcomingEvents = [...experience.events]
    .filter((event) => event.startsAt)
    .sort(compareEventDisplayOrder);
  const nextEvent = upcomingEvents[0] ?? null;
  const daysRemaining = nextEvent?.startsAt
    ? daysUntil(nextEvent.startsAt)
    : null;

  const rsvpCtaTarget =
    website.entry_flow.cta_target?.startsWith('/') === true
      ? website.entry_flow.cta_target
      : '/access';
  const rsvpCtaLabel =
    website.entry_flow.cta_label ?? msg('pages.guest_landing_page.rsvp_cta');

  const navItems = website.nav_items
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((item) => ({
      id: item.id,
      label: item.label,
      href: resolveWebsiteNavPath(item),
    }));

  const siteTitle =
    website.identity.site_title_override ?? experience.occasion.displayName;
  const tagline =
    website.identity.subtitle_tagline ?? stringField(heroSection, 'subtitle');
  const heroBody =
    stringField(heroSection, 'body') ?? stringField(heroSection, 'description');

  return (
    <div className="guest-landing">
      <nav className="guest-landing__nav">
        <span className="guest-landing__wordmark">{siteTitle}</span>
        {navItems.length > 0 ? (
          <ul className="guest-landing__nav-links">
            {navItems.map((item) => (
              <li key={item.id}>
                <NavLink to={item.href}>{item.label}</NavLink>
              </li>
            ))}
          </ul>
        ) : null}
        <NavLink className="guest-landing__nav-cta" to={rsvpCtaTarget}>
          {rsvpCtaLabel}
        </NavLink>
      </nav>

      <section className="guest-landing__hero">
        <p className="guest-landing__eyebrow">
          {msg('pages.guest_landing_page.hero_eyebrow')}
        </p>
        <h1>{siteTitle}</h1>
        {tagline ? <p className="guest-landing__subline">{tagline}</p> : null}
        {nextEvent ? (
          <p className="guest-landing__details">
            {formatGuestDateTime(
              nextEvent.startsAt as string,
              localeCode,
              timezone,
            )}
            {nextEvent.locationName ? ` — ${nextEvent.locationName}` : ''}
          </p>
        ) : null}
        {heroBody ? <p className="guest-landing__tagline">{heroBody}</p> : null}
        <div className="guest-landing__btn-row">
          <NavLink
            className="guest-landing__btn guest-landing__btn--primary"
            to={rsvpCtaTarget}
          >
            {rsvpCtaLabel}
          </NavLink>
          {upcomingEvents.length > 0 ? (
            <NavLink
              className="guest-landing__btn guest-landing__btn--outline"
              to="/events"
            >
              {msg('pages.guest_landing_page.events_view_full_schedule')}
            </NavLink>
          ) : null}
        </div>
      </section>

      {daysRemaining !== null && daysRemaining >= 0 ? (
        <div className="guest-landing__countdown">
          <b>{daysRemaining}</b>{' '}
          {daysRemaining === 1
            ? msg('pages.guest_landing_page.countdown_day')
            : msg('pages.guest_landing_page.countdown_days')}{' '}
          {msg('pages.guest_landing_page.countdown_until_the_celebration')}
        </div>
      ) : null}

      {storySection ? (
        <section className="guest-landing__story">
          <p className="guest-landing__eyebrow">
            {sectionLabel(
              storySection,
              msg('pages.guest_landing_page.story_eyebrow'),
            )}
          </p>
          <p className="guest-landing__pull">{sectionBody(storySection)}</p>
        </section>
      ) : null}

      {upcomingEvents.length > 0 ? (
        <section className="guest-landing__events">
          <p className="guest-landing__eyebrow">
            {msg('pages.guest_landing_page.events_eyebrow')}
          </p>
          <div className="guest-landing__events-grid">
            {upcomingEvents.slice(0, 4).map((event) => (
              <article className="guest-landing__event-card" key={event.id}>
                <p className="guest-landing__event-tag">
                  {formatGuestDateTime(
                    event.startsAt as string,
                    localeCode,
                    timezone,
                  )}
                </p>
                <h3>{event.displayName}</h3>
                {event.locationName || event.locationAddress ? (
                  <p className="guest-landing__event-meta">
                    {[event.locationName, event.locationAddress]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                ) : null}
                {event.guestSummary ? (
                  <p className="guest-landing__event-meta">
                    {event.guestSummary}
                  </p>
                ) : null}
              </article>
            ))}
          </div>
          <div className="guest-landing__more-link">
            <NavLink to="/events">
              {msg('pages.guest_landing_page.events_view_full_schedule')}
            </NavLink>
          </div>
        </section>
      ) : null}

      <section className="guest-landing__rsvp">
        <p className="guest-landing__eyebrow">
          {msg('pages.guest_landing_page.rsvp_eyebrow')}
        </p>
        <h2>
          {rsvpSection
            ? sectionLabel(
                rsvpSection,
                msg('pages.guest_landing_page.rsvp_default_title'),
              )
            : msg('pages.guest_landing_page.rsvp_default_title')}
        </h2>
        <p>
          {rsvpSection
            ? sectionBody(rsvpSection)
            : msg('pages.guest_landing_page.rsvp_default_body')}
        </p>
        <NavLink
          className="guest-landing__btn guest-landing__btn--primary"
          to={rsvpCtaTarget}
        >
          {rsvpCtaLabel}
        </NavLink>
      </section>

      {travelSections.length > 0 ? (
        <section className="guest-landing__travel">
          <p className="guest-landing__eyebrow">
            {msg('pages.guest_landing_page.travel_eyebrow')}
          </p>
          <div className="guest-landing__travel-grid">
            {travelSections.map((section) => (
              <article className="guest-landing__travel-card" key={section.id}>
                <span className="guest-landing__travel-name">
                  {sectionLabel(
                    section,
                    msg('pages.guest_landing_page.travel_eyebrow'),
                  )}
                </span>
                <span className="guest-landing__travel-dist">
                  {sectionBody(section)}
                </span>
              </article>
            ))}
          </div>
          <div className="guest-landing__more-link">
            <NavLink to="/locations">
              {msg('pages.guest_landing_page.travel_cta')}
            </NavLink>
          </div>
        </section>
      ) : null}

      {registrySection ? (
        <section className="guest-landing__registry">
          <p className="guest-landing__eyebrow">
            {msg('pages.guest_landing_page.registry_eyebrow')}
          </p>
          <p>{sectionBody(registrySection)}</p>
          <NavLink
            className="guest-landing__btn guest-landing__btn--outline"
            to={getSectionTargetPath(registrySection)}
          >
            {msg('pages.guest_landing_page.registry_cta')}
          </NavLink>
        </section>
      ) : null}

      {gallerySection ? (
        <section className="guest-landing__gallery">
          <p className="guest-landing__eyebrow">
            {msg('pages.guest_landing_page.gallery_eyebrow')}
          </p>
          <p>{sectionBody(gallerySection)}</p>
          <NavLink
            className="guest-landing__btn guest-landing__btn--outline"
            to={getSectionTargetPath(gallerySection)}
          >
            {msg('pages.guest_landing_page.gallery_cta')}
          </NavLink>
        </section>
      ) : null}

      {faqSections.length > 0 ? (
        <section className="guest-landing__faq">
          <p className="guest-landing__eyebrow guest-landing__eyebrow--center">
            {msg('pages.guest_landing_page.faq_eyebrow')}
          </p>
          {faqSections.map((section) => (
            <details className="guest-landing__faq-item" key={section.id}>
              <summary>
                <span>{sectionLabel(section, section.section_key)}</span>
                <TovIcon name="caret-down" />
              </summary>
              <p className="guest-landing__faq-answer">
                {sectionBody(section)}
              </p>
            </details>
          ))}
        </section>
      ) : null}

      <footer className="guest-landing__footer">
        <p className="guest-landing__sign">{siteTitle}</p>
        <NavLink className="guest-landing__fine" to="/help">
          {msg('pages.guest_landing_page.footer_contact')}
        </NavLink>
        <p className="guest-landing__powered">
          {msg('pages.guest_landing_page.footer_tagline')}
        </p>
      </footer>
    </div>
  );
}
